import { AutomationExecutionStatus, AutomationStatus } from "../../config/enums";
import { RelatedRecord, RelatedTableActions } from "../relatedTableActions.types";
import { canSendAutomationExecutionNow, getAutomationExecutionModeStatusLabel } from "./executions/automationExecutionRules";
import { formatAutomationCountValue, formatAutomationRateValue } from "./automationFormat";
import { mapAutomationExecutionTableRecords } from "./executions/automationExecutionRows";
import { prepareAutomationCommunication } from "./executions/prepareAutomationCommunication";
import { executionEntityName } from "./automationExecutionTabs";

// The services and helpers are loaded when the executions are read, so registering every
// page doesn't pull the automation graph into each one.

export const statusVariants: Record<string, string> = {
  Claimed: "info",
  Pending: "info",
  Processing: "info",
  Queued: "info",
  Completed: "success",
  Cancelled: "danger",
  Expired: "danger",
  Failed: "danger",
};

const upcomingStatuses: number[] = [
  AutomationExecutionStatus.Pending,
  AutomationExecutionStatus.Claimed,
  AutomationExecutionStatus.Processing,
  AutomationExecutionStatus.Queued,
];

const failed = (result: { response?: unknown; status?: number } | undefined) =>
  Boolean(result?.response || (result?.status && result.status >= 400));

/** The automation's executions as related-table rows, or null if they can't be read. */
export async function readExecutionRows(
  globalDispatch: unknown,
  automationId: string
): Promise<RelatedRecord[] | null> {
  const { GetAutomationExecutions } = await import("./automationService");
  const response = await GetAutomationExecutions(globalDispatch, automationId);

  if (!response?.data) {
    return null;
  }

  return (
    mapAutomationExecutionTableRecords(response.data) as RelatedRecord[]
  ).map((execution) => {
    const fields = execution.Fields;
    const statusName = String(fields.statusName ?? "");
    const mode = getAutomationExecutionModeStatusLabel(
      fields.executionAutomationStatus ?? fields.automationStatus
    );

    return {
      ...execution,
      Fields: {
        ...fields,
        phase: upcomingStatuses.includes(Number(fields.status))
          ? "upcoming"
          : "completed",
        statusLabel: fields.statusLabel || statusName || "--",
        statusVariant: statusVariants[statusName] ?? "secondary",
        modeLabel: mode,
        modeVariant:
          String(fields.executionAutomationStatus ?? fields.automationStatus) ===
          String(AutomationStatus.Testing)
            ? "warning"
            : "success",
        fixtureName: fields.fixtureId ? fields.fixtureName : "",
        emailsSentText: formatAutomationCountValue(fields.emailsSent),
        openRateText: formatAutomationRateValue(fields.openRate),
        clickRateText: formatAutomationRateValue(fields.clickRate),
        clicksToOpenRateText: formatAutomationRateValue(fields.clicksToOpenRate),
      },
    };
  });
}

const rowActions: NonNullable<RelatedTableActions["rowActions"]> = [
  {
    id: "update-content",
    label: "Update content",
    variant: "primary",
    visible: (row) => Boolean(row.updateCommunicationLink || row.canPrepare),
    run: async ({ globalDispatch, parentId, row, navigate }) => {
      const link = row.updateCommunicationLink as { to: string } | null;

      if (link) {
        navigate(link.to);

        return null;
      }

      const result = await prepareAutomationCommunication({
        automationId: row.automationId,
        dispatch: globalDispatch as null,
        fixtureId: row.fixtureId,
        navigate,
      });

      return result.redirected ? null : readExecutionRows(globalDispatch, parentId);
    },
  },
  {
    id: "send-now",
    label: "Send now",
    variant: "outline-primary",
    visible: (row) => canSendAutomationExecutionNow(row),
    confirmation: () => ({
      title: "Send automation now",
      confirmText: "Send now",
      message:
        "Update this automation execution send time to now? The automation agent can pick it up on its next poll.",
    }),
    run: async ({ globalDispatch, parentId, row }) => {
      const { SendAutomationExecutionNow } = await import("./automationService");
      const result = await SendAutomationExecutionNow(globalDispatch, row.id);

      if (failed(result)) {
        throw result;
      }

      return readExecutionRows(globalDispatch, parentId);
    },
  },
];

export const automationExecutionTableActions: Record<string, RelatedTableActions> = {
  [executionEntityName]: {
    rowActions,
    refresh: ({ globalDispatch, parentId }) => readExecutionRows(globalDispatch, parentId),
  },
};
